'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { useSSOStore } from '@/lib/sso/store';
import type { SSOConfig, OIDCConfigInput, SSOProvider } from '@/lib/sso/types';
import { Copy, Check, Eye, EyeOff } from 'lucide-react';

interface OIDCConfigFormProps {
  config: SSOConfig | null;
  provider: SSOProvider;
  onCancel?: () => void;
}

const DEFAULT_SCOPES = ['openid', 'email', 'profile'];

const PROVIDER_PRESETS: Record<string, { issuer: string; scopes: string[] }> = {
  okta: {
    issuer: 'https://your-domain.okta.com',
    scopes: ['openid', 'email', 'profile', 'groups'],
  },
  azure_ad: {
    issuer: 'https://login.microsoftonline.com/{tenant_id}/v2.0',
    scopes: ['openid', 'email', 'profile', 'User.Read'],
  },
  google_workspace: {
    issuer: 'https://accounts.google.com',
    scopes: ['openid', 'email', 'profile'],
  },
};

export function OIDCConfigForm({ config, provider, onCancel }: OIDCConfigFormProps) {
  const { saveSSOConfig, ssoLoading } = useSSOStore();
  const [copied, setCopied] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const preset = PROVIDER_PRESETS[provider];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<OIDCConfigInput>({
    defaultValues: {
      provider: provider as OIDCConfigInput['provider'],
      display_name: config?.display_name || '',
      oidc_issuer: config?.oidc_issuer || preset?.issuer || '',
      oidc_client_id: config?.oidc_client_id || '',
      oidc_client_secret: '',
      oidc_scopes: config?.oidc_scopes || preset?.scopes || DEFAULT_SCOPES,
      oidc_claim_mapping: config?.oidc_claim_mapping || {
        email: 'email',
        firstName: 'given_name',
        lastName: 'family_name',
      },
      allowed_domains: config?.allowed_domains || [],
      auto_provision_users: config?.auto_provision_users ?? true,
      default_role: config?.default_role || 'member',
      jit_provisioning: config?.jit_provisioning ?? true,
    },
  });

  const onSubmit = async (data: OIDCConfigInput) => {
    try {
      await saveSSOConfig(data);
    } catch (error) {
      // Error handled by store
    }
  };

  const handleCopyCallback = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/v1/sso/oidc/callback`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (scope: string) => {
    const currentScopes = watch('oidc_scopes') || [];
    if (currentScopes.includes(scope)) {
      setValue('oidc_scopes', currentScopes.filter((s) => s !== scope));
    } else {
      setValue('oidc_scopes', [...currentScopes, scope]);
    }
  };

  const currentScopes = watch('oidc_scopes') || [];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Callback URL (Read-only) */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-sm">Callback URL</h4>
        <p className="text-sm text-muted-foreground">
          Add this URL to your identity provider's allowed redirect URIs
        </p>
        <div className="flex items-center gap-2">
          <Input
            value={`${window.location.origin}/api/v1/sso/oidc/callback`}
            readOnly
            className="bg-background"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleCopyCallback}
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
      </div>

      {/* Basic Settings */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            {...register('display_name')}
            placeholder="Company SSO"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="oidc_issuer">
            Issuer URL <span className="text-destructive">*</span>
          </Label>
          <Input
            id="oidc_issuer"
            {...register('oidc_issuer', { required: 'Issuer URL is required' })}
            placeholder={preset?.issuer || 'https://your-idp.com'}
            className="mt-1"
          />
          {errors.oidc_issuer && (
            <p className="text-sm text-destructive mt-1">{errors.oidc_issuer.message}</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            The OpenID Connect issuer URL (without /.well-known/openid-configuration)
          </p>
        </div>

        <div>
          <Label htmlFor="oidc_client_id">
            Client ID <span className="text-destructive">*</span>
          </Label>
          <Input
            id="oidc_client_id"
            {...register('oidc_client_id', { required: 'Client ID is required' })}
            placeholder="your-client-id"
            className="mt-1"
          />
          {errors.oidc_client_id && (
            <p className="text-sm text-destructive mt-1">{errors.oidc_client_id.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="oidc_client_secret">
            Client Secret <span className="text-destructive">*</span>
          </Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              id="oidc_client_secret"
              type={showSecret ? 'text' : 'password'}
              {...register('oidc_client_secret', {
                required: !config ? 'Client Secret is required' : false,
              })}
              placeholder={config ? '••••••••' : 'your-client-secret'}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowSecret(!showSecret)}
            >
              {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </Button>
          </div>
          {errors.oidc_client_secret && (
            <p className="text-sm text-destructive mt-1">{errors.oidc_client_secret.message}</p>
          )}
          {config && (
            <p className="text-sm text-muted-foreground mt-1">
              Leave blank to keep existing secret
            </p>
          )}
        </div>

        <div>
          <Label>Scopes</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {['openid', 'email', 'profile', 'groups', 'offline_access'].map((scope) => (
              <Badge
                key={scope}
                variant={currentScopes.includes(scope) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleScope(scope)}
              >
                {scope}
              </Badge>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Click to toggle scopes. 'openid' is required.
          </p>
        </div>
      </div>

      {/* Advanced Settings */}
      <Accordion type="single" collapsible>
        <AccordionItem value="claim-mapping">
          <AccordionTrigger>Claim Mapping</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Map OIDC claims to user profile fields
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email Claim</Label>
                  <Input
                    value={watch('oidc_claim_mapping')?.email || ''}
                    onChange={(e) => setValue('oidc_claim_mapping', {
                      ...watch('oidc_claim_mapping'),
                      email: e.target.value,
                    })}
                    placeholder="email"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>First Name Claim</Label>
                  <Input
                    value={watch('oidc_claim_mapping')?.firstName || ''}
                    onChange={(e) => setValue('oidc_claim_mapping', {
                      ...watch('oidc_claim_mapping'),
                      firstName: e.target.value,
                    })}
                    placeholder="given_name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Last Name Claim</Label>
                  <Input
                    value={watch('oidc_claim_mapping')?.lastName || ''}
                    onChange={(e) => setValue('oidc_claim_mapping', {
                      ...watch('oidc_claim_mapping'),
                      lastName: e.target.value,
                    })}
                    placeholder="family_name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Groups Claim</Label>
                  <Input
                    value={watch('oidc_claim_mapping')?.groups || ''}
                    onChange={(e) => setValue('oidc_claim_mapping', {
                      ...watch('oidc_claim_mapping'),
                      groups: e.target.value,
                    })}
                    placeholder="groups"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="provisioning">
          <AccordionTrigger>User Provisioning</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-provision Users</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically create accounts for new SSO users
                  </p>
                </div>
                <Switch
                  checked={watch('auto_provision_users')}
                  onCheckedChange={(checked) => setValue('auto_provision_users', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>JIT Provisioning</Label>
                  <p className="text-sm text-muted-foreground">
                    Update user attributes on each login
                  </p>
                </div>
                <Switch
                  checked={watch('jit_provisioning')}
                  onCheckedChange={(checked) => setValue('jit_provisioning', checked)}
                />
              </div>

              <div>
                <Label>Default Role</Label>
                <Select
                  value={watch('default_role')}
                  onValueChange={(value) => setValue('default_role', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={ssoLoading || (!isDirty && !!config)}>
          {ssoLoading ? 'Saving...' : config ? 'Update Configuration' : 'Save Configuration'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
